import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRequireRole } from "@/hooks/useRequireRole";

const PageFallback = () => (
  <div className="min-h-svh bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground text-sm">Loading…</p>
    </div>
  </div>
);

const ConnectionError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="min-h-svh bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-4 text-center px-4">
      <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
        <span className="text-yellow-500 text-xl">&#9888;</span>
      </div>
      <h2 className="text-lg font-semibold text-foreground">Connection Issue</h2>
      <p className="text-muted-foreground text-sm max-w-md">
        We're having trouble connecting to our servers right now. This is usually temporary. Please try again in a moment.
      </p>
      <button
        onClick={onRetry}
        className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        Try Again
      </button>
    </div>
  </div>
);

/** Cross-tab Web Lock steal: another tab took over the auth session lock.
 *  Plain-language screen instead of the generic Connection Issue copy, which
 *  read as a server outage when the actual cause was a second tab (cowork
 *  audit 07-23 §6.5). */
const MultiTabPaused = ({ onRetry }: { onRetry: () => void }) => (
  <div className="min-h-svh bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-4 text-center px-4">
      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
        <span className="text-primary text-xl" aria-hidden="true">&#9432;</span>
      </div>
      <h2 className="text-lg font-semibold text-foreground">Open in another tab</h2>
      <p className="text-muted-foreground text-sm max-w-md">
        Cipher &amp; Row is active in another browser tab, so this tab paused to
        avoid session conflicts. Close the other tab, or click below to continue
        here.
      </p>
      <button
        onClick={onRetry}
        className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        Continue in this tab
      </button>
    </div>
  </div>
);

const AccessDenied = () => (
  <div className="min-h-svh bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-3 text-center px-4">
      <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
        <span className="text-destructive text-xl">!</span>
      </div>
      <h2 className="text-lg font-semibold text-foreground">Access Denied</h2>
      <p className="text-muted-foreground text-sm max-w-md">
        You do not have permission to view this page. Contact your administrator if you believe this is an error.
      </p>
    </div>
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "moderator" | "user";
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading, connectionError, multiTabConflict, retryConnection, aal } = useAuth();
  const { hasRole, loading: roleLoading } = useRequireRole(requiredRole ?? "user");
  const location = useLocation();

  // Multi-tab is the more specific diagnosis - check it before the generic
  // connection screen so cross-tab lock loss never reads as a server outage.
  if (multiTabConflict) return <MultiTabPaused onRetry={retryConnection} />;
  if (connectionError) return <ConnectionError onRetry={retryConnection} />;
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  // Defense-in-depth MFA gate: even if a sign-in path forgets to route through
  // /mfa-verify, every authenticated route bounces an AAL1 user with a pending
  // AAL2 challenge here. Login.tsx and AuthCallback.tsx are the primary
  // enforcement points; this catches refreshes, deep links, and any future
  // sign-in flow added later.
  if (aal?.currentLevel === "aal1" && aal?.nextLevel === "aal2") {
    return <Navigate to="/mfa-verify" replace />;
  }

  if (requiredRole) {
    if (roleLoading) return <PageFallback />;
    if (!hasRole) return <AccessDenied />;
  }

  return <>{children}</>;
}
